import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_DETAILS = {
  name: '',
  email: '',
  phone: '',
  visitType: 'new-patient',
  reason: '',
};

export const nextOpenDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

export const useAppointmentBooking = () => {
  const [selectedDate, setSelectedDate] = useState(nextOpenDate);
  const [availability, setAvailability] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState('');
  const [details, setDetails] = useState(INITIAL_DETAILS);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // API is a module-level constant; setters are stable per React
  const loadAvailability = useCallback(async (date) => {
    setLoadingSlots(true);
    setSelectedTime('');
    try {
      const res = await axios.get(`${API}/appointments/availability`, { params: { date } });
      setAvailability(res.data);
    } catch {
      toast.error('Could not load available times. Please try again.');
      setAvailability(null);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => {
    if (selectedDate) loadAvailability(selectedDate);
  }, [selectedDate, loadAvailability]);

  const handleDetailChange = useCallback((e) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  }, []);

  const bookAppointment = useCallback(async (e) => {
    e.preventDefault();
    if (!selectedTime) {
      toast.error('Please pick a time slot first.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/appointments`, {
        ...details,
        date: selectedDate,
        time: selectedTime,
      });
      setConfirmation(res.data);
      setDetails(INITIAL_DETAILS);
      toast.success('Appointment reserved! Check your email for confirmation.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Booking failed. Please call us at (281) 886-8595.');
      if (err.response?.status === 409) loadAvailability(selectedDate);
    } finally {
      setSubmitting(false);
    }
  }, [details, selectedDate, selectedTime, loadAvailability]);

  const resetBooking = useCallback(() => {
    setConfirmation(null);
    setSelectedTime('');
    loadAvailability(selectedDate);
  }, [selectedDate, loadAvailability]);

  return {
    selectedDate,
    setSelectedDate,
    availability,
    loadingSlots,
    selectedTime,
    setSelectedTime,
    details,
    handleDetailChange,
    submitting,
    confirmation,
    bookAppointment,
    resetBooking,
  };
};
